import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { ListingStatusSheet, parseMarketplaceLinks } from "@/components/ListingStatusSheet";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import type { MarketplaceLink } from "@/lib/marketplaces";

type ScanRow = {
  id: string;
  user_id: string;
  title: string | null;
  brand: string | null;
  category: string | null;
  photos: string[] | null;
  result: unknown;
  post_kind: string | null;
  marketplace_links: unknown;
  sold_at: string | null;
  sold_price: number | null;
  // "Which marketplace did this sell on" lives in posted_to — the sheet
  // surfaces it as soldOn but the DB column is named after the listing
  // platform field it was repurposed from.
  posted_to: string | null;
  is_deadstock: boolean | null;
};

export default function ListingStatusEdit() {
  const nav = useNavigate();
  const { scanId } = useParams<{ scanId: string }>();
  const { session } = useAuth();
  const [scan, setScan] = useState<ScanRow | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!scanId) {
      setError("missing scan id");
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error: qErr } = await supabase
        .from("scans")
        .select("id, user_id, title, brand, category, photos, result, post_kind, marketplace_links, sold_at, sold_price, posted_to, is_deadstock")
        .eq("id", scanId)
        .maybeSingle();
      if (cancelled) return;
      if (qErr || !data) {
        setError("Couldn't load this listing.");
        return;
      }
      setScan(data as unknown as ScanRow);
    })();
    return () => { cancelled = true; };
  }, [scanId]);

  const goBack = () => {
    if (window.history.length > 1) nav(-1);
    else nav("/you");
  };

  // RLS already restricts UPDATE on scans to the owner, but we bail early
  // here if a non-owner reaches this URL — the sheet would error on save.
  const isOwner = !!scan && !!session?.user?.id && scan.user_id === session.user.id;

  if (error) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink))" }}>
            {error}
            <div style={{ marginTop: 16 }}>
              <button
                onClick={goBack}
                style={{
                  background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                  border: "1px solid hsl(var(--ink))", padding: "10px 18px",
                  cursor: "pointer", fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
                }}
              >Go back</button>
            </div>
          </div>
        </div>
      </MobileFrame>
    );
  }

  if (!scan) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div className="caps" style={{ color: "hsl(var(--ink-2))" }}>Loading…</div>
        </div>
      </MobileFrame>
    );
  }

  if (!isOwner) {
    return (
      <MobileFrame>
        <div className="paper-bg" style={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center" }}>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", color: "hsl(var(--ink))" }}>
            You can only edit your own listings.
            <div style={{ marginTop: 16 }}>
              <button
                onClick={goBack}
                style={{
                  background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))",
                  border: "1px solid hsl(var(--ink))", padding: "10px 18px",
                  cursor: "pointer", fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
                }}
              >Go back</button>
            </div>
          </div>
        </div>
      </MobileFrame>
    );
  }

  const initialLinks: MarketplaceLink[] = parseMarketplaceLinks(scan.marketplace_links);
  const photos = (scan.photos ?? []).filter((p): p is string => typeof p === "string" && !!p);
  // Mirror the Profile tile's "unverified" detection so the same items that
  // render the "indeterminate" / unverified state in the closet also surface
  // the Verify-now CTA here. Quick posts carry post_kind="manual" and may
  // still have a result blob from a partial run.
  const unverified = !scan.result || scan.post_kind === "manual";

  return (
    <MobileFrame>
      <ListingStatusSheet
        mode="page"
        open
        scanId={scan.id}
        scanTitle={scan.title ?? "This scan"}
        initialLinks={initialLinks}
        initialDeadstock={!!scan.is_deadstock}
        initialSold={{
          soldAt: scan.sold_at,
          soldPrice: scan.sold_price,
          soldOn: scan.posted_to,
        }}
        scanPhotos={photos}
        scanBrand={scan.brand}
        scanCategory={scan.category}
        isUnverified={unverified}
        onClose={goBack}
        onSaved={() => {
          // Sheet finished a save / delete / unpost — route back. The
          // closet list refreshes on next mount from the data hook.
          goBack();
        }}
      />
    </MobileFrame>
  );
}
